import React, { useState, useEffect } from 'react';
import { Package, Clock, User, Phone, AlertCircle, Printer, CheckCircle, XCircle, RefreshCw, FileText } from 'lucide-react';
import axios from 'axios';

import PageContainer from '../layout/PageContainer';
interface OrderItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

interface TakeoutOrder {
  id: number;
  order_number: string;
  customer_name: string;
  customer_phone: string;
  order_items: string;
  orderItems?: OrderItem[];
  total_amount: number;
  pickup_time: string;
  payment_method: string;
  payment_status: string;
  order_status: string;
  special_requests?: string;
  order_type: string;
  created_at: string;
}

interface ProductionSummary {
  [key: string]: {
    name: string;
    totalQuantity: number;
    orders: Array<{
      orderNumber: string;
      quantity: number;
      pickupTime: string;
    }>;
  };
}

const BentoOrderManagement: React.FC = () => {
  const [orders, setOrders] = useState<TakeoutOrder[]>([]);
  const [productionSheet, setProductionSheet] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [refreshKey, setRefreshKey] = useState(0);

  const API_BASE = `http://${window.location.hostname}:3001`;

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 30000); // 30秒ごとに自動更新
    return () => clearInterval(interval);
  }, [refreshKey]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/api/takeout/orders/today`);
      if (response.data.success) {
        const ordersWithParsedItems = response.data.orders.map((order: TakeoutOrder) => ({
          ...order,
          orderItems: typeof order.order_items === 'string' 
            ? JSON.parse(order.order_items) 
            : order.order_items
        }));
        setOrders(ordersWithParsedItems);
      }
    } catch (error) {
      console.error('注文取得エラー:', error);
    }
    setLoading(false);
  };

  const generateProductionSheet = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/takeout/production-sheet`);
      if (response.data.success) {
        setProductionSheet(response.data.sheet);
        // 印刷用HTMLを生成
        printProductionSheet(response.data.sheet);
      }
    } catch (error) {
      console.error('製造指示書生成エラー:', error);
    }
  };

  const printProductionSheet = (sheet: any) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>製造指示書 - ${sheet.date}</title>
        <style>
          body { 
            font-family: 'メイリオ', sans-serif; 
            padding: 20px;
            max-width: 800px;
            margin: 0 auto;
          }
          h1 { 
            border-bottom: 3px solid #333; 
            padding-bottom: 10px;
            margin-bottom: 20px;
          }
          .header {
            display: flex;
            justify-content: space-between;
            margin-bottom: 30px;
          }
          .summary-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 30px;
          }
          .summary-table th, .summary-table td {
            border: 1px solid #ddd;
            padding: 10px;
            text-align: left;
          }
          .summary-table th {
            background: #f5f5f5;
            font-weight: bold;
          }
          .order-detail {
            margin-bottom: 20px;
            padding: 15px;
            border: 1px solid #ddd;
            page-break-inside: avoid;
          }
          .order-header {
            background: #f9f9f9;
            padding: 10px;
            margin: -15px -15px 10px -15px;
            font-weight: bold;
          }
          .special-request {
            background: #fff3cd;
            padding: 10px;
            margin-top: 10px;
            border-radius: 5px;
          }
          @media print {
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <h1>🍱 弁当製造指示書</h1>
        <div class="header">
          <div>製造日: ${new Date(sheet.date).toLocaleDateString('ja-JP')}</div>
          <div>印刷時刻: ${new Date().toLocaleTimeString('ja-JP')}</div>
          <div>総注文数: ${sheet.totalOrders}件</div>
        </div>
        
        <h2>📊 製造数量サマリー</h2>
        <table class="summary-table">
          <thead>
            <tr>
              <th>メニュー名</th>
              <th>必要数量</th>
              <th>注文内訳</th>
            </tr>
          </thead>
          <tbody>
            ${Object.entries(sheet.summary as ProductionSummary).map(([id, item]) => `
              <tr>
                <td><strong>${item.name}</strong></td>
                <td style="text-align: center; font-size: 1.2em;"><strong>${item.totalQuantity}</strong></td>
                <td>
                  ${item.orders.map(o => 
                    `${o.orderNumber}(${o.quantity}個・${o.pickupTime})`
                  ).join('<br>')}
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <h2>📝 注文詳細</h2>
        ${sheet.orderDetails.map((order: any) => `
          <div class="order-detail">
            <div class="order-header">
              注文番号: ${order.orderNumber} | ${order.customerName}様 | 受取: ${order.pickupTime}
            </div>
            <ul>
              ${order.items.map((item: OrderItem) => 
                `<li>${item.name} × ${item.quantity}個</li>`
              ).join('')}
            </ul>
            ${order.specialRequests ? `
              <div class="special-request">
                ⚠️ 特記事項: ${order.specialRequests}
              </div>
            ` : ''}
          </div>
        `).join('')}
        
        <div style="margin-top: 50px; padding-top: 20px; border-top: 1px solid #ddd;">
          <p>製造完了チェック: □</p>
          <p>製造担当者: _______________</p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const updateOrderStatus = async (orderNumber: string, newStatus: string) => {
    try {
      const response = await axios.put(`${API_BASE}/api/takeout/order/${orderNumber}/status`, {
        status: newStatus
      });
      
      if (response.data.success) {
        fetchOrders(); // リロード
      }
    } catch (error) {
      console.error('ステータス更新エラー:', error);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: { [key: string]: { color: string; icon: JSX.Element; label: string } } = {
      pending: { 
        color: 'bg-yellow-100 text-yellow-800', 
        icon: <Clock className="w-4 h-4" />,
        label: '準備中'
      },
      confirmed: { 
        color: 'bg-blue-100 text-blue-800', 
        icon: <CheckCircle className="w-4 h-4" />,
        label: '確認済'
      },
      preparing: { 
        color: 'bg-orange-100 text-orange-800', 
        icon: <Package className="w-4 h-4" />,
        label: '調理中'
      },
      ready: { 
        color: 'bg-green-100 text-green-800', 
        icon: <CheckCircle className="w-4 h-4" />,
        label: '完成'
      },
      delivered: { 
        color: 'bg-gray-100 text-gray-800', 
        icon: <CheckCircle className="w-4 h-4" />,
        label: '受渡済'
      },
      cancelled: { 
        color: 'bg-red-100 text-red-800', 
        icon: <XCircle className="w-4 h-4" />,
        label: 'キャンセル'
      }
    };

    const statusInfo = statusMap[status] || statusMap.pending;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
        {statusInfo.icon}
        {statusInfo.label}
      </span>
    );
  };

  const getPaymentBadge = (method: string) => {
    return method === 'cash' ? (
      <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs">現金</span>
    ) : (
      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">PayPay</span>
    );
  };

  const filteredOrders = selectedStatus === 'all' 
    ? orders 
    : orders.filter(order => order.order_status === selectedStatus);

  // 事前予約分のみカウント
  const advanceOrdersCount = orders.filter(o => 
    o.order_type === 'advance' && o.order_status === 'confirmed'
  ).length;

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">🍱 弁当予約管理</h2>
            <p className="text-gray-600 mt-1">
              本日の予約: {orders.length}件 
              （事前予約: {advanceOrdersCount}件）
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setRefreshKey(prev => prev + 1)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              更新
            </button>
            <button
              onClick={generateProductionSheet}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              disabled={advanceOrdersCount === 0}
            >
              <Printer className="w-4 h-4" />
              製造指示書印刷
            </button>
          </div>
        </div>

        {/* 締切警告 */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-yellow-400 mr-2" />
            <p className="text-sm text-yellow-800">
              事前予約受付は午前10時で締切です。10時になったら製造指示書を印刷してください。
            </p>
          </div>
        </div>

        {/* ステータスフィルター */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setSelectedStatus('all')}
            className={`px-3 py-1 rounded-lg text-sm ${
              selectedStatus === 'all' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            全て ({orders.length})
          </button>
          <button
            onClick={() => setSelectedStatus('confirmed')}
            className={`px-3 py-1 rounded-lg text-sm ${
              selectedStatus === 'confirmed' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            確認済 ({orders.filter(o => o.order_status === 'confirmed').length})
          </button>
          <button
            onClick={() => setSelectedStatus('preparing')}
            className={`px-3 py-1 rounded-lg text-sm ${
              selectedStatus === 'preparing' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            調理中 ({orders.filter(o => o.order_status === 'preparing').length})
          </button>
          <button
            onClick={() => setSelectedStatus('ready')}
            className={`px-3 py-1 rounded-lg text-sm ${
              selectedStatus === 'ready' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            完成 ({orders.filter(o => o.order_status === 'ready').length})
          </button>
        </div>
      </div>

      {/* 注文一覧 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">読み込み中...</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Package className="w-12 h-12 mx-auto mb-2 text-gray-400" />
            <p>注文はありません</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    注文番号
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    顧客情報
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    注文内容
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    受取時間
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    金額
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ステータス
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOrders.map((order) => (
                  <tr key={order.order_number} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {order.order_number}
                      </div>
                      <div className="text-xs text-gray-500">
                        {order.order_type === 'advance' ? '事前予約' : '当日'}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900">{order.customer_name}</span>
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <Phone className="w-3 h-3 text-gray-400" />
                        <span className="text-xs text-gray-500">{order.customer_phone}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900">
                        {order.orderItems?.map((item, idx) => (
                          <div key={idx}>
                            {item.name} × {item.quantity}
                          </div>
                        ))}
                      </div>
                      {order.special_requests && (
                        <div className="mt-1 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded">
                          ⚠️ {order.special_requests}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900">{order.pickup_time}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        ¥{order.total_amount.toLocaleString()}
                      </div>
                      <div className="mt-1">
                        {getPaymentBadge(order.payment_method)}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {getStatusBadge(order.order_status)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <select
                        value={order.order_status}
                        onChange={(e) => updateOrderStatus(order.order_number, e.target.value)}
                        className="text-sm border rounded px-2 py-1"
                      >
                        <option value="confirmed">確認済</option>
                        <option value="preparing">調理中</option>
                        <option value="ready">完成</option>
                        <option value="delivered">受渡済</option>
                        <option value="cancelled">キャンセル</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 製造指示書プレビュー（モーダル表示可能） */}
      {productionSheet && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold mb-4">製造指示書プレビュー</h3>
          <div className="border rounded p-4">
            <div className="mb-4">
              <p>製造日: {new Date(productionSheet.date).toLocaleDateString('ja-JP')}</p>
              <p>総注文数: {productionSheet.totalOrders}件</p>
            </div>
            <div className="space-y-2">
              {Object.entries(productionSheet.summary as ProductionSummary).map(([id, item]) => (
                <div key={id} className="flex justify-between border-b pb-2">
                  <span className="font-medium">{item.name}</span>
                  <span className="font-bold text-lg">{item.totalQuantity}個</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BentoOrderManagement;